import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'

import type { DisplayPrecision } from '../types/panel-fields'

/** The W/H "constrain" link: reset per selection, mirrored to the manager, auto-shown during corner drags. */
export function useDimensionLink(params: {
  activeObjects: ShapeData[]
  shapeManagerRef?: React.RefObject<ShapeManagerRef | null>
  isCornerResizeActive: boolean
  setDisplayPrecision: React.Dispatch<React.SetStateAction<DisplayPrecision>>
}) {
  const { activeObjects, shapeManagerRef, isCornerResizeActive, setDisplayPrecision } = params
  const [dimensionsLinked, setDimensionsLinked] = useState(false)
  /** Set while `dimensionsLinked` was auto-enabled by a corner-handle drag (not the user), so release reverts it. */
  const autoLinkedForCornerResizeRef = useRef(false)

  const selectionIdsKey = activeObjects
    .map((s) => s.id)
    .sort()
    .join('\0')

  useEffect(() => {
    setDimensionsLinked(false)
    autoLinkedForCornerResizeRef.current = false
    shapeManagerRef?.current?.setPanelDimensionsLinked(false)
    setDisplayPrecision({})
  }, [selectionIdsKey, shapeManagerRef, setDisplayPrecision])

  useEffect(() => {
    shapeManagerRef?.current?.setPanelDimensionsLinked(dimensionsLinked)
  }, [dimensionsLinked, shapeManagerRef])

  // A corner-handle drag is always aspect-locked regardless of this toggle (see
  // `shouldUseAspectRatio`) — this effect only makes the panel's link button *reflect* that
  // temporarily, so the UI doesn't look out of sync with what's actually happening on canvas.
  useEffect(() => {
    if (isCornerResizeActive) {
      if (!dimensionsLinked) {
        autoLinkedForCornerResizeRef.current = true
        setDimensionsLinked(true)
      }
    } else if (autoLinkedForCornerResizeRef.current) {
      autoLinkedForCornerResizeRef.current = false
      setDimensionsLinked(false)
    }
  }, [isCornerResizeActive, dimensionsLinked])

  return { dimensionsLinked, setDimensionsLinked }
}
